/**
 * Personalização modal — intercept ATC (spec 0055 / ADR-035).
 * Line item properties: Nome-Personalizado, Fonte-Nome.
 */
import { addVariantToCart } from './gm-add-to-cart.js';

const NAME_KEY = 'Nome-Personalizado';
const FONT_KEY = 'Fonte-Nome';
const MAX_NAME = 10;

/** @type {{ variantId: string, quantity: number, productId: string } | null} */
let pending = null;

/**
 * @returns {import('./dialog.js').DialogComponent | HTMLElement | null}
 */
function getRoot() {
  return /** @type {any} */ (document.getElementById('gm-personalization-modal'));
}

/**
 * @param {any} root
 * @param {DOMStringMap} data
 */
function fillHeader(root, data) {
  const titleEl = root.querySelector('[data-gm-pers-title]');
  const colorEl = root.querySelector('[data-gm-pers-color]');
  const priceEl = root.querySelector('[data-gm-pers-price]');
  const thumbWrap = root.querySelector('[data-gm-pers-thumb]');
  const thumbImg = root.querySelector('[data-gm-pers-thumb-img]');

  if (titleEl) titleEl.textContent = data.gmPersTitle || '';
  if (priceEl) priceEl.textContent = data.gmPersPrice || '';

  if (colorEl) {
    const color = data.gmPersColor || '';
    colorEl.textContent = color;
    colorEl.hidden = !color;
  }

  if (thumbWrap && thumbImg) {
    const src = data.gmPersImage || '';
    if (src) {
      thumbImg.src = src;
      thumbImg.alt = data.gmPersTitle || '';
      thumbWrap.hidden = false;
    } else {
      thumbImg.removeAttribute('src');
      thumbWrap.hidden = true;
    }
  }
}

/**
 * @param {HTMLElement} root
 */
function resetForm(root) {
  const form = root.querySelector('[data-gm-pers-form]');
  if (form instanceof HTMLFormElement) form.reset();
  const err = root.querySelector('[data-gm-pers-error]');
  if (err) {
    err.hidden = true;
    err.textContent = '';
  }
}

/**
 * @param {HTMLElement} root
 * @param {string} message
 */
function showError(root, message) {
  const err = root.querySelector('[data-gm-pers-error]');
  if (!err) return;
  err.textContent = message;
  err.hidden = !message;
}

/**
 * @param {HTMLElement} host
 */
function openForHost(host) {
  const root = getRoot();
  if (!root) return;

  const variantId =
    host.dataset.variantId ||
    host.querySelector('input[name="id"]')?.value ||
    host.querySelector('[data-variant-id]')?.dataset?.variantId;
  if (!variantId) return;

  const qtyInput = host.querySelector('input[name="quantity"]');
  const quantity =
    Number(host.dataset.quantity) ||
    Number(qtyInput?.value) ||
    1;
  const productId = host.dataset.productId || '';

  pending = { variantId, quantity, productId };
  fillHeader(root, host.dataset);
  resetForm(root);

  if (typeof root.showDialog === 'function') {
    root.showDialog();
  } else {
    const dialog = root.querySelector('dialog');
    dialog?.showModal();
  }

  const nome = root.querySelector('[data-gm-pers-nome]');
  if (nome instanceof HTMLInputElement) {
    requestAnimationFrame(() => nome.focus());
  }
}

function closeModal() {
  const root = getRoot();
  if (!root) return;
  if (typeof root.closeDialog === 'function') {
    root.closeDialog();
  } else {
    root.querySelector('dialog')?.close();
  }
  pending = null;
}

/**
 * @param {Record<string, string> | null} properties
 */
async function confirmAdd(properties) {
  if (!pending) return;
  const { variantId, quantity, productId } = pending;
  const root = getRoot();
  const submitBtn = root?.querySelector('[data-gm-pers-submit]');
  if (submitBtn instanceof HTMLButtonElement) {
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-busy', 'true');
  }

  try {
    await addVariantToCart({
      variantId,
      quantity,
      productId,
      properties: properties || undefined,
    });
    closeModal();
  } catch (err) {
    console.error('[gm-personalization]', err);
    if (root) {
      showError(
        root,
        root.dataset.errorAdd || 'Não foi possível adicionar à sacola.'
      );
    }
  } finally {
    if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = false;
      submitBtn.removeAttribute('aria-busy');
    }
  }
}

/**
 * @param {Element | null} el
 * @returns {HTMLElement | null}
 */
function personalizableHost(el) {
  if (!(el instanceof Element)) return null;
  const host = el.closest('[data-gm-personalizable="true"]');
  return host instanceof HTMLElement ? host : null;
}

document.addEventListener(
  'click',
  (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const cardBtn = target.closest('[data-gm-add-to-cart][data-gm-personalizable="true"]');
    if (cardBtn instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      openForHost(cardBtn);
      return;
    }

    const pdpBtn = target.closest(
      'product-form-component[data-gm-personalizable="true"] button[type="submit"][name="add"]'
    );
    if (pdpBtn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const host = personalizableHost(pdpBtn);
      if (host) openForHost(host);
    }
  },
  true
);

document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type !== 'add-to-cart-form') return;
    const host = personalizableHost(form);
    if (!host) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    openForHost(host);
  },
  true
);

function bindModalUi() {
  const root = getRoot();
  if (!root || root.dataset.gmPersBound === 'true') return;
  root.dataset.gmPersBound = 'true';

  const form = root.querySelector('[data-gm-pers-form]');
  const skip = root.querySelector('[data-gm-pers-skip]');
  const nomeInput = root.querySelector('[data-gm-pers-nome]');

  if (nomeInput instanceof HTMLInputElement) {
    nomeInput.addEventListener('input', () => {
      if (nomeInput.value.length > MAX_NAME) {
        nomeInput.value = nomeInput.value.slice(0, MAX_NAME);
      }
    });
  }

  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    const nome = (root.querySelector('[data-gm-pers-nome]')?.value || '').trim();
    const fonteChecked = root.querySelector('[data-gm-pers-fonte]:checked');
    const fonte =
      (fonteChecked instanceof HTMLInputElement ? fonteChecked.value : '') ||
      '';

    if (nome && !fonte.trim()) {
      showError(
        root,
        root.dataset.errorFont || 'Selecione a fonte do nome.'
      );
      return;
    }

    if (!nome) {
      confirmAdd(null);
      return;
    }

    confirmAdd({
      [NAME_KEY]: nome.slice(0, MAX_NAME),
      [FONT_KEY]: fonte.trim(),
    });
  });

  skip?.addEventListener('click', () => {
    confirmAdd(null);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindModalUi);
} else {
  bindModalUi();
}
